import Image from 'next/image';
import Link from 'next/link';
import Trans from 'next-translate/Trans';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  delay,
  tone,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { PHOTOS, useAboutTranslation } from './kit';

export default function AboutHero() {
  const { t } = useAboutTranslation();

  const stats = [
    { value: '3.4M+', label: t('people have signed up') },
    { value: '150+', label: t('countries') },
    { value: '100+', label: t('languages') },
    { value: '300+', label: t('AI tools and templates') },
  ];

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pt-12 sm:pt-20 lg:px-8'>
        <a
          href='#year-2026'
          className={clsxm(
            styles.enter,
            'group inline-flex max-w-full items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] transition hover:ring-[#0f0f28]/20 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:ring-white/25',
            tone.body,
          )}
        >
          <span
            dir='ltr'
            className='flex-none rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold tabular-nums text-white'
          >
            84/100
          </span>
          <span className='truncate'>
            {t('Joint top AI trust score in Asia, Cybernews 2026')}
          </span>
          <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
        </a>

        <div className='mt-8 grid gap-x-14 gap-y-8 lg:grid-cols-12 lg:items-end'>
          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              'text-[2.875rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:col-span-7 lg:text-[5.25rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            <Trans
              i18nKey='hero-headline'
              ns='about-us'
              defaultTrans='Making AI <0>easy-peasy</0> for everyone.'
              components={[
                <span
                  key='0'
                  // Never split the brand word at its hyphen.
                  className='whitespace-nowrap text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </h1>

          <div
            className={clsxm(styles.enter, 'lg:col-span-5 lg:pb-2')}
            style={delay(160)}
          >
            <p
              className={clsxm(
                'text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              {t(
                'Easy-Peasy.AI is an AI platform built in Singapore by Marianna and Dmytro Olefyrenko. People use it to write, design, make video and voice, and put AI agents to work.',
              )}
            </p>
            <div className='mt-8 flex flex-wrap items-center gap-3'>
              <Link href='/login#auth-sign-up' className={buttonPrimary}>
                {t('Start for free')}
                <ArrowGlyph />
              </Link>
              <a href='#founders' className={buttonSecondary}>
                {t('Meet the founders')}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.enter,
          'mx-auto mt-14 max-w-7xl px-3 sm:mt-20 sm:px-6 lg:px-8',
        )}
        style={delay(240)}
      >
        <figure className='relative aspect-[4/3] overflow-hidden rounded-[28px] bg-[#e8e8f0] sm:aspect-[16/9] sm:rounded-[36px] lg:aspect-[2.35/1] dark:bg-white/5'>
          <Image
            src={PHOTOS.foundersAtGoogle}
            alt={t(
              'Marianna and Dmytro Olefyrenko, the founders of Easy-Peasy.AI, in Easy-Peasy.AI T-shirts',
            )}
            fill
            priority
            unoptimized
            className='object-cover object-[50%_40%]'
          />
          <figcaption className='absolute start-4 top-4 rounded-full bg-white/90 px-3.5 py-1.5 text-[13px] font-medium text-[#10101c] shadow-[0_6px_20px_-8px_rgba(15,15,40,0.45)] backdrop-blur sm:start-6 sm:top-6'>
            {t('Our founders, Marianna and Dmytro')}
          </figcaption>
        </figure>

        <dl className='relative mx-3 -mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-[22px] bg-[#e7e7ef] shadow-[0_24px_50px_-28px_rgba(30,27,75,0.45)] ring-1 ring-[#0f0f28]/[0.07] sm:mx-8 sm:-mt-14 lg:mx-auto lg:max-w-4xl lg:grid-cols-4 dark:bg-[#26253a] dark:ring-white/[0.08]'>
          {stats.map(({ value, label }) => (
            <div
              key={value}
              className={clsxm(
                'flex flex-col px-5 py-5 sm:px-7 sm:py-6',
                tone.surface,
              )}
            >
              <dt className={clsxm('mt-2 text-sm leading-5', tone.quiet)}>
                {label}
              </dt>
              {/* Shown above its label; the label stays first for readers. */}
              <dd
                className={clsxm(
                  styles.display,
                  'order-first text-3xl font-semibold tabular-nums sm:text-[2.5rem] sm:leading-none',
                  tone.ink,
                )}
              >
                <bdi dir='ltr'>{value}</bdi>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </header>
  );
}
